import React, {useState} from 'react'
import {formatDateToBrazilian} from '../../../../../_metronic/helpers/AssetHelpers'
import './PublicClient.css'

interface Task {
  id: number
  name: string
  priority: string
  status: string
  project_name: string
  total_time: number
  users: Array<{
    name: string
    avatar: string | null
  }>
  hasTag: boolean
  hasChecklist: boolean
  hasDeadline: boolean
  expected_date?: string
  isOverdue?: boolean
  color?: string
}

interface PublicKanbanCardProps {
  task: Task
  formatTime: (minutes: number) => string
  showDeliveryFooter?: boolean
}

// Mesmas cores de prioridade do card do kanban interno.
const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical':
    case 'critica':
      return 'badge-danger'
    case 'high':
    case 'alta':
      return 'badge-warning'
    case 'medium':
    case 'media':
      return 'badge-info'
    case 'low':
    case 'baixa':
      return 'badge-success'
    default:
      return 'badge-secondary'
  }
}

const getPriorityText = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical':
    case 'critica':
      return 'Crítica'
    case 'high':
    case 'alta':
      return 'Alta'
    case 'medium':
    case 'media':
      return 'Média'
    case 'low':
    case 'baixa':
      return 'Baixa'
    default:
      return 'N/A'
  }
}

const getInitials = (name: string) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')

// Foto do usuário quando existe; senão (ou se a imagem falhar) as iniciais, sem chamar serviço externo.
const TaskAvatar: React.FC<{user: {name: string; avatar: string | null}}> = ({user}) => {
  const [failed, setFailed] = useState(false)
  const apiUrl = process.env.REACT_APP_THEME_API_URL || process.env.REACT_APP_API_URL || ''
  const hasAvatar =
    typeof user.avatar === 'string' &&
    user.avatar.trim() !== '' &&
    user.avatar !== 'null' &&
    user.avatar !== 'undefined'

  if (!hasAvatar || failed) {
    return (
      <span className='pc-avatar' title={user.name}>
        {getInitials(user.name)}
      </span>
    )
  }

  const avatarUrl = /^https?:\/\//.test(user.avatar!)
    ? user.avatar!
    : `${apiUrl}/storage/${user.avatar}`

  return (
    <img
      src={avatarUrl}
      alt={user.name}
      title={user.name}
      className='pc-avatar'
      onError={() => setFailed(true)}
    />
  )
}

const PublicKanbanCard: React.FC<PublicKanbanCardProps> = ({
  task,
  formatTime,
  showDeliveryFooter = false,
}) => {
  const deadline = task.expected_date
    ? formatDateToBrazilian(String(task.expected_date).slice(0, 10))
    : ''

  return (
    <div className='card border-0 shadow-sm'>
      <div className='card-body p-3'>
        <div className='d-flex justify-content-between align-items-start gap-2 mb-2'>
          <h6 className='pc-kcard-title card-title mb-0 fs-5 fw-bold text-dark'>{task.name}</h6>
          <span className={`badge ${getPriorityColor(task.priority)} fs-7 flex-shrink-0`}>
            {getPriorityText(task.priority)}
          </span>
        </div>

        {(task.hasTag || task.hasChecklist) && (
          <div className='d-flex gap-2 mb-2'>
            {task.hasTag && (
              <span className='text-gray-600' title='Esta tarefa possui uma tag associada'>
                <i className='bi bi-tag-fill fs-6'></i>
              </span>
            )}
            {task.hasChecklist && (
              <span className='text-success' title='Esta tarefa possui checklist de itens'>
                <i className='bi bi-check2-square fs-6'></i>
              </span>
            )}
          </div>
        )}

        <div className='d-flex justify-content-between align-items-center flex-nowrap gap-2'>
          <div className='d-flex align-items-center gap-2 flex-grow-1 min-w-0 flex-nowrap'>
            <small className='text-muted fs-7 flex-shrink-0'>#{task.id}</small>
            {task.users.length > 0 && (
              <span className='pc-avatars flex-shrink-0'>
                {task.users.map((user, index) => (
                  <TaskAvatar key={index} user={user} />
                ))}
              </span>
            )}
          </div>
          <small className='d-flex align-items-center text-muted fs-7 flex-shrink-0'>
            <i className='bi bi-clock me-1 fs-6'></i>
            {formatTime(Math.round((task.total_time ?? 0) / 60))}
          </small>
        </div>

        {/* Rodapé de prazo: em "Entregue" nunca mostra atraso (igual ao kanban interno). */}
        {deadline && (
          <div className='mt-2 pt-2 border-top'>
            {showDeliveryFooter ? (
              <small className='text-success fs-7'>
                <i className='bi bi-check-circle me-1 fs-6'></i>
                Prazo: {deadline}
              </small>
            ) : task.isOverdue ? (
              <small className='text-danger fs-7'>
                <i className='bi bi-calendar-event me-1 fs-6'></i>
                Tarefa atrasada: {deadline}
              </small>
            ) : (
              <small className='text-warning fs-7'>
                <i className='bi bi-calendar-event me-1 fs-6'></i>
                Prazo definido: {deadline}
              </small>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export {PublicKanbanCard}
